import type { ComponentProps } from 'react';
import type { PlainObject } from 'simplytyped';
import { z } from 'zod';

import type { TProductFeeData } from '../../common';
import type { Combine } from '../../common/types';
import { ZeefiProductTypes } from '../../common/types/ProductCatalogue';
import type {
  Block,
  InputArrayPrimitiveType,
  Select,
  TableCellType,
  TTableCellContent
} from '../atoms';
import type { EditSchemaModalProps, SimpleTableRow } from '../molecules';
import type { EditableTable } from '../organisms/EditableTable';

export type TRange = {
  min: number;
  max: number;
};

type TFrequency = {
  frequency: 'Weekly' | 'Fortnightly' | 'Monthly';
  amount: number;
};

type TLoanAmount = TRange & {
  amount?: number;
  frequencies?: ReadonlyArray<TFrequency>;
};

export type TFeeTerm = TRange & {
  unit?: 'month' | string;
  amount?: number;
  loanAmounts?: ReadonlyArray<TLoanAmount>;
};

type TCondition = {
  terms?: ReadonlyArray<TFeeTerm>;
};

export enum FEE_IDS {
  EstablishmentUpfrontFee = 'EstablishmentUpfrontFee',
  AccountServicePlannedFee = 'AccountServicePlannedFee',
  DefaultFee = 'DefaultFee',
  MerchantServiceFee = 'MerchantServiceFee',
  ArrearsFee = 'ArrearsFee',
  DepositFee = 'DepositFee'
}

export const FEE_LABELS = {
  [FEE_IDS.EstablishmentUpfrontFee]: 'Establishment Fees',
  [FEE_IDS.AccountServicePlannedFee]: 'Account Servicing Fees',
  [FEE_IDS.DefaultFee]: 'Late Fees',
  [FEE_IDS.MerchantServiceFee]: 'Merchant Service Fees',
  [FEE_IDS.ArrearsFee]: 'In Arrears Fees',
  [FEE_IDS.DepositFee]: 'Deposit Fees'
};

interface TArrearsFee extends TFee {
  id: FEE_IDS.ArrearsFee;
  name: 'Arrears Fee';
}

interface TEstablishmentUpfrontFee extends TFee {
  id: FEE_IDS.EstablishmentUpfrontFee;
  name: 'Establishment Fee Upfront';
  // conditions?: TCondition;
}

interface TDepositFee extends TFee {
  id: FEE_IDS.DepositFee;
  name: 'Deposit Fee';
}

interface TAccountServicePlannedFee extends TFee {
  id: FEE_IDS.AccountServicePlannedFee;
  name: 'Account Service Planned Fee';
  // conditions: TCondition;
}

interface TDefaultFee extends TFee {
  id: FEE_IDS.DefaultFee;
  name: 'Default Fee';
}

interface TMerchantServiceFee extends TFee {
  id: FEE_IDS.MerchantServiceFee;
  name: 'Merchant Service Fee';
  // conditions: TCondition;
}

type TFeeBand = {
  loanAmountRange: TRange;
  termFees: ReadonlyArray<string | number>;
};

export type TIndexedFees = {
  termRanges: Array<TRange>;
  feeBands: ReadonlyArray<TFeeBand>;
};

type TFee = {
  // NOTE: intentionally left overwriting types as it helps provide examples
  state: 'ACTIVE' | string;
  amount: number;
  type: 'FLAT' | string;
  required?: boolean;
  feePaidBy: 'student' | 'EP' | string;
  gst?: number;
  productFeeKey?: string;
  amortized: boolean;
  systemCreationMethod: 'Manual' | string;
  daysInArrears?: number;
  externalPaymentSourceId?: string;
  conditions?: TCondition;
  feeMatrix?: TIndexedFees;
};

export type TFees =
  | TMerchantServiceFee
  | TDefaultFee
  | TAccountServicePlannedFee
  | TEstablishmentUpfrontFee
  | TDepositFee
  | TArrearsFee
  | {
      id: string;
      name: string;
    };

export type FeeTypeKeys = { id: TFees['id'] };

export type TFeeSnapshot = {
  externalId: string;
  productId: string;
  fees: Record<string, TProductFeeData>;
};

const AccountProductSchema = z.object({
  productCatalogueId: z.nativeEnum(ZeefiProductTypes, {
    required_error: 'productCatalogueId is required',
    invalid_type_error: 'productCatalogueId must be a string'
  }),
  code: z.string({
    required_error: 'code is required',
    invalid_type_error: 'code must be a string'
  }),
  description: z
    .string({
      invalid_type_error: 'description must be a string'
    })
    .optional()
});

export const ProductDescriptions: { [type: string]: string } = {
  SNP: 'SNPL dynamic student loan',
  PAS: 'Payment Assist product'
} as const;

export type TAccountProductType = z.infer<typeof AccountProductSchema>;

type _AdditionalBlockRowProps = {
  content:
    | ComponentProps<typeof SimpleTableRow>['content']
    | (() => ComponentProps<typeof SimpleTableRow>['content']);
  hidden?: boolean;
  // name?: Path<Schema>; // commented out due to memory reasons
  name?: string;
  editingOptions?: {
    options?: ComponentProps<typeof Select>['options'];
    complexOptions?: ComponentProps<typeof Select>['complexOptions'];
    type?: InputArrayPrimitiveType;
    required?: boolean;
  };
  contentType: Exclude<TableCellType, 'array' | 'table'>;
  nonEditable?: boolean;
};

type NonArrayAccountProductBlockRow = Combine<
  ComponentProps<typeof SimpleTableRow>,
  _AdditionalBlockRowProps
>;

type _TableBlockRowProps = {
  // name: ArrayPath<Schema>;
  // name?: ArrayPath<Schema>; // commented out due to memory reasons
  name?: string;
  contentType: 'table';
  content: Array<Record<string, TTableCellContent['content']>>;
  tableConfig: Omit<
    ComponentProps<typeof EditableTable>,
    'name' | 'control' | 'register'
  >;
};

type TableAccountProductBlockRow = Combine<
  NonArrayAccountProductBlockRow,
  _TableBlockRowProps
>;

export type ArrayAccountProductBlockRow = Combine<
  NonArrayAccountProductBlockRow,
  {
    defaultValue: any;
    contentType: 'array';
    // name?: Path<Schema>; // commented out due to memory reasons
    name?: string;
    itemContentType: Exclude<TableCellType, 'array'>;
    hideAddButton?: boolean;
    hideDeleteButton?: boolean;
  }
>;

export type AccountProductBlockRow =
  | NonArrayAccountProductBlockRow
  | ArrayAccountProductBlockRow
  | TableAccountProductBlockRow;

interface AccountProductBlockBase {
  size: ComponentProps<typeof Block>['size'];
  title?: string;
  hidden?: boolean;
  schema: PlainObject;
  modal: {
    columns: EditSchemaModalProps['columns'];
    parseData: EditSchemaModalProps['parseData'];
    onSubmit: EditSchemaModalProps['onSubmit'];
  };
  editBlock?: {
    onEdit: () => Promise<unknown> | unknown;
    label: string;
  };
}

interface AccountProductBlockStandard extends AccountProductBlockBase {
  id: 'product' | 'merchant';
}

interface AccountProductBlockTable extends AccountProductBlockBase {
  id: FEE_IDS;
  matrix: {
    FooterBlock?: () => React.ReactNode;
    FooterInitial?: () => React.ReactNode;
  };
}

export type AccountProductBlock =
  | AccountProductBlockStandard
  | AccountProductBlockTable;
